

import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchParkAttendance,
  deleteParkAttendance,
} from "../redux/actions/ParkAttendanceActions";

import { fetchDistricts, fetchMunicipalities } from "../redux/actions/locationActions";
import { fetchDesignations } from "../redux/actions/designationActions";

import theme from "../components/shared/theme";
import {
  tdStyle,
  thStyle,
  paginationButtonStyle,
  dropdownStyle,
} from "../components/shared/themeStyles";

import Papa from "papaparse";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const GOOGLE_MAPS_KEY = process.env.REACT_APP_MAP_API_KEY;
const itemsPerPage = 10;

const ParkAttendance = () => {
  const dispatch = useDispatch();

  // Use safe defaults in case redux slices are undefined
  const parkAttendanceState = useSelector((state) => state.parkAttendance || {});
  const { attendance = [], loading = false, error = null, pagination = {} } = parkAttendanceState;

  const locationState = useSelector((state) => state.location || {});
  const { districts = [], municipalities = [] } = locationState;

  const designationState = useSelector((state) => state.designation || {});
  const { designations = [] } = designationState;

  // Filters & UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [selectedDesignation, setSelectedDesignation] = useState("");
  const [selectedProjectType, setSelectedProjectType] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Modals
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");

  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapCoords, setMapCoords] = useState(null); // { lat, lng }
  const [mapAddress, setMapAddress] = useState("");
  const [mapLoading, setMapLoading] = useState(false);

  const [deleteId, setDeleteId] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Derived values: selected district id & filtered municipalities
  const selectedDistrictObj = useMemo(
    () => districts.find((d) => d.name === selectedDistrict) || null,
    [districts, selectedDistrict]
  );
  const selectedDistrictId = selectedDistrictObj?.id;

  const filteredMunicipalities = useMemo(
    () =>
      selectedDistrictId
        ? municipalities.filter((m) => m.district_id === selectedDistrictId)
        : municipalities,
    [municipalities, selectedDistrictId]
  );

  // Fetch initial data for filters
  useEffect(() => {
    dispatch(fetchDistricts());
    dispatch(fetchMunicipalities());
    dispatch(fetchDesignations());
  }, [dispatch]);

  // Fetch attendance whenever filters / page change
  useEffect(() => {
    dispatch(
      fetchParkAttendance({
        page: currentPage,
        limit: itemsPerPage,
        district: selectedDistrict,
        municipality: selectedMunicipality,
        fromDate,
        toDate,
        search: searchTerm,
        designation: selectedDesignation,
        project_type: selectedProjectType,
      })
    );
  }, [
    dispatch,
    currentPage,
    selectedDistrict,
    selectedMunicipality,
    fromDate,
    toDate,
    searchTerm,
    selectedDesignation,
    selectedProjectType,
  ]);

  // Reset municipality when district changes
  useEffect(() => {
    setSelectedMunicipality("");
  }, [selectedDistrict]);

  // Helpers
  const formatDate = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString("en-IN");
  };

  // Photo Modal
  const openPhotoModal = (url) => {
    setPhotoUrl(url);
    setPhotoModalOpen(true);
  };
  const closePhotoModal = () => {
    setPhotoModalOpen(false);
    setPhotoUrl("");
  };

  // Map Modal: open with coords, get address via reverse geocode if key present
  const openMapModal = async (lat, lng) => {
    setMapCoords({ lat, lng });
    setMapModalOpen(true);
    setMapLoading(true);
    setMapAddress("Loading...");

    if (!GOOGLE_MAPS_KEY) {
      // No API key — skip reverse geocode and show basic message
      setMapAddress("Address lookup unavailable (no API key). Map preview is shown below.");
      setMapLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_KEY}`
      );
      const data = await res.json();
      if (data.status === "OK" && data.results && data.results.length > 0) {
        setMapAddress(data.results[0].formatted_address || "No address found");
      } else {
        setMapAddress("Unable to fetch address");
      }
    } catch (err) {
      setMapAddress("Error fetching address");
    } finally {
      setMapLoading(false);
    }
  };

  const closeMapModal = () => {
    setMapModalOpen(false);
    setMapCoords(null);
    setMapAddress("");
    setMapLoading(false);
  };

  // Delete flow
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    // dispatch delete action
    await dispatch(deleteParkAttendance(deleteId));
    setConfirmDeleteOpen(false);
    setDeleteId(null);
    // refetch current page
    dispatch(
      fetchParkAttendance({
        page: currentPage,
        limit: itemsPerPage,
        district: selectedDistrict,
        municipality: selectedMunicipality,
        fromDate,
        toDate,
        search: searchTerm,
        designation: selectedDesignation,
        project_type: selectedProjectType,
      })
    );
  };

  // CSV Export
  const handleDownloadCSV = async () => {
    try {
      const token = localStorage.getItem("token") || "";
      const queryParams = new URLSearchParams({
        page: 1,
        limit: 10000,
        district: selectedDistrict,
        municipality: selectedMunicipality,
        fromDate,
        toDate,
        search: searchTerm,
        designation: selectedDesignation,
        project_type: selectedProjectType,
      }).toString();

      const response = await fetch(
        `${BASE_URL}/v1/api/admin/getAllParkAttendance?${queryParams}`,
        {
          headers: {
            authorization: token,
          },
        }
      );
      const json = await response.json();
      const rows = json?.data || [];

      const csvRows = [
        [
          "ID",
          "Date",
          "Employee Name",
          "Phone",
          "Email",
          "Designation",
          "Project Type",
          "Project Code",
          "Check-In Time",
          "Check-In Lat",
          "Check-In Long",
          "Check-Out Time",
          "Check-Out Lat",
          "Check-Out Long",
          "Status",
          "Remarks",
        ],
      ];

      rows.forEach((att) => {
        csvRows.push([
          att.id,
          att.date,
          att.employee_name,
          att.employee_phone,
          att.employee_email,
          att.designation,
          att.project_type || "",
          att.project_code,
          att.check_in,
          att.check_in_latitude,
          att.check_in_longitude,
          att.check_out,
          att.check_out_latitude,
          att.check_out_longitude,
          att.status,
          att.remarks,
        ]);
      });

      const csv = Papa.unparse(csvRows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "Park_Attendance.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("CSV download failed.");
    }
  };

  return (
    <div
      style={{
        padding: theme.spacing.lg,
        backgroundColor: theme.colors.background,
        minHeight: "100vh",
      }}
    >
      {/* Embedded small CSS for keyframes & minor styles */}
      <style>{`
        @keyframes modalPop {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* small responsive tweak for iframe map */
        .park-map-iframe { border: 0; width: 100%; height: 300px; border-radius: 8px; }
      `}</style>

      <h1
        style={{
          fontSize: theme.font.size["3xl"],
          fontWeight: theme.font.weight.bold,
          color: theme.colors.grayDark,
          marginBottom: theme.spacing.lg,
        }}
      >
        Park Attendance Records
      </h1>

      {/* FILTERS */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: theme.spacing.sm,
          marginBottom: theme.spacing.lg,
          backgroundColor: "#fff",
          padding: theme.spacing.md,
          borderRadius: theme.radius.xl,
          boxShadow: theme.shadow.soft,
        }}
      >
        <select
          value={selectedDistrict}
          onChange={(e) => setSelectedDistrict(e.target.value)}
          style={dropdownStyle}
        >
          <option value="">All Districts</option>
          {districts.map((d) => (
            <option key={d.id} value={d.name}>
              {d.name}
            </option>
          ))}
        </select>

        <select
          value={selectedMunicipality}
          onChange={(e) => setSelectedMunicipality(e.target.value)}
          style={dropdownStyle}
        >
          <option value="">All Municipalities</option>
          {filteredMunicipalities.map((m) => (
            <option key={m.id} value={m.name}>
              {m.name}
            </option>
          ))}
        </select>

        <select
          value={selectedDesignation}
          onChange={(e) => setSelectedDesignation(e.target.value)}
          style={dropdownStyle}
        >
          <option value="">All Designations</option>
          {designations.map((d, i) => (
            <option key={i} value={d}>
              {d}
            </option>
          ))}
        </select>

        <select
          value={selectedProjectType}
          onChange={(e) => setSelectedProjectType(e.target.value)}
          style={dropdownStyle}
        >
          <option value="">Project Types</option>
          <option value="Park">Park</option>
          <option value="Shelter">Shelter</option>
          <option value="Stretch">Stretch</option>
        </select>

        <input
          type="text"
          value={searchTerm}
          placeholder="Search..."
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ ...dropdownStyle, minWidth: "220px" }}
        />

        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          style={dropdownStyle}
        />

        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          style={dropdownStyle}
        />

        <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
          <button
            onClick={() => {
              setFromDate("");
              setToDate("");
              setSelectedDistrict("");
              setSelectedMunicipality("");
              setSelectedDesignation("");
              setSelectedProjectType("");
              setSearchTerm("");
              setCurrentPage(1);
            }}
            style={{
              backgroundColor: theme.colors.primary,
              color: "#fff",
              padding: theme.spacing.sm,
              borderRadius: theme.radius.md,
              border: "none",
              cursor: "pointer",
            }}
          >
            Reset
          </button>

          <button
            onClick={handleDownloadCSV}
            style={{
              backgroundColor: theme.colors.secondary,
              color: "#fff",
              padding: theme.spacing.sm,
              borderRadius: theme.radius.md,
              border: "none",
              cursor: "pointer",
            }}
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: theme.radius.xl,
          boxShadow: theme.shadow.soft,
          overflowX: "auto",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            minWidth: "1200px",
            fontSize: theme.font.size.sm,
          }}
        >
          <thead style={{ backgroundColor: theme.colors.grayDark, color: "#fff" }}>
            <tr>
              <th style={thStyle}>ID</th>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Phone</th>
              {/* <th style={thStyle}>Email</th> */}
              <th style={thStyle}>Designation</th>
              <th style={thStyle}>Project Type</th>
              <th style={thStyle}>Project Code</th>
              <th style={thStyle}>In / Out</th>
              <th style={thStyle}>Check-In Address</th>
              <th style={thStyle}>Check-Out Address</th>
              <th style={thStyle}>Photo</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Remarks</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={14} style={{ textAlign: "center", padding: 20 }}>
                  Loading...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={14} style={{ color: "red", textAlign: "center" }}>
                  {error}
                </td>
              </tr>
            ) : attendance.length === 0 ? (
              <tr>
                <td colSpan={14} style={{ textAlign: "center" }}>
                  No Records Found
                </td>
              </tr>
            ) : (
              attendance.map((att) => (
                <tr key={att.id}>
                  <td style={tdStyle}>{att.id}</td>
                  <td style={tdStyle}>{formatDate(att.date)}</td>
                  <td style={tdStyle}>{att.employee_name}</td>
                  <td style={tdStyle}>{att.employee_phone}</td>
                  {/* <td style={tdStyle}>{att.employee_email || "-"}</td> */}
                  <td style={tdStyle}>{att.designation}</td>
                  
                  <td style={tdStyle}>
                    {att.project_type ? (
                      <span
                        style={{
                          backgroundColor:
                            att.project_type === "Park"
                              ? "#4CAF50"
                              : att.project_type === "Shelter"
                              ? "#2196F3"
                              : att.project_type === "Stretch"
                              ? "#FF9800"
                              : "#9E9E9E",
                          color: "#fff",
                          padding: "4px 8px",
                          borderRadius: "12px",
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          display: "inline-block",
                        }}
                      >
                        {att.project_type}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                    <td style={tdStyle}>{att.project_code}</td>
                  <td style={tdStyle}>
                    {att.check_in || "-"} / {att.check_out || "-"}
                  </td>

                  <td style={tdStyle}>
                    {att.check_in_latitude && att.check_in_longitude ? (
                      <button
                        onClick={() =>
                          openMapModal(att.check_in_latitude, att.check_in_longitude)
                        }
                        style={{
                          color: theme.colors.primary,
                          textDecoration: "underline",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        View Address
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>

                  <td style={tdStyle}>
                    {att.check_out_latitude && att.check_out_longitude ? (
                      <button
                        onClick={() =>
                          openMapModal(att.check_out_latitude, att.check_out_longitude)
                        }
                        style={{
                          color: theme.colors.primary,
                          textDecoration: "underline",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        View Address
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>

                  <td style={tdStyle}>
                    {att.check_in_photo || att.check_out_photo ? (
                      <>
                        {att.check_in_photo && (
                          <button
                            onClick={() => openPhotoModal(att.check_in_photo)}
                            style={{
                              color: theme.colors.primary,
                              textDecoration: "underline",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                            }}
                          >
                            In
                          </button>
                        )}

                        {att.check_out_photo && (
                          <button
                            onClick={() => openPhotoModal(att.check_out_photo)}
                            style={{
                              marginLeft: 8,
                              color: theme.colors.primary,
                              textDecoration: "underline",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                            }}
                          >
                            Out
                          </button>
                        )}
                      </>
                    ) : (
                      "-"
                    )}
                  </td>

                  <td style={tdStyle}>{att.status}</td>
                  <td style={tdStyle}>{att.remarks || att.check_out_remarks || "-"}</td>

                  <td style={tdStyle}>
                    <button
                      onClick={() => handleDeleteClick(att.id)}
                      style={{
                        color: "#fff",
                        background: "#e53935",
                        border: "none",
                        padding: "6px 10px",
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* PAGINATION */}
        <div
          style={{
            padding: theme.spacing.md,
            backgroundColor: theme.colors.grayLight,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <button
              onClick={() => {
                setCurrentPage((p) => {
                  const next = Math.max(1, p - 1);
                  // dispatch new page fetch
                  return next;
                });
              }}
              disabled={currentPage === 1}
              style={paginationButtonStyle}
            >
              Previous
            </button>
          </div>

          <p style={{ margin: 0 }}>
            Page {pagination?.currentPage || currentPage} of {pagination?.totalPages || 1}
          </p>

          <div>
            <button
              onClick={() =>
                setCurrentPage((p) => Math.min(pagination?.totalPages || 1, p + 1))
              }
              disabled={currentPage === pagination?.totalPages}
              style={paginationButtonStyle}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* PHOTO MODAL */}
      {photoModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 20,
          }}
          onClick={closePhotoModal}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 18,
              maxWidth: "90%",
              width: 800,
              maxHeight: "90vh",
              boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
              animation: "modalPop 180ms ease",
              position: "relative",
              overflow: "auto",
            }}
          >
            <button
              onClick={closePhotoModal}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                background: "none",
                border: "none",
                fontSize: 20,
                cursor: "pointer",
                color: "#444",
              }}
              aria-label="Close photo modal"
            >
              ✖
            </button>

            <div style={{ display: "flex", justifyContent: "center" }}>
              <img
                src={photoUrl}
                alt="Attendance"
                style={{ maxWidth: "100%", maxHeight: "80vh", borderRadius: 8 }}
              />
            </div>
          </div>
        </div>
      )}

      {/* MAP MODAL (with live preview + address + actions) */}
      {mapModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 20,
          }}
          onClick={closeMapModal}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 18,
              maxWidth: "95%",
              width: 720,
              boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
              animation: "modalPop 180ms ease",
              position: "relative",
            }}
          >
            <button
              onClick={closeMapModal}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                background: "none",
                border: "none",
                fontSize: 20,
                cursor: "pointer",
                color: "#444",
              }}
              aria-label="Close map modal"
            >
              ✖
            </button>

            <h2 style={{ marginTop: 0, color: theme.colors.grayDark }}>Location Address</h2>

            <div style={{ display: "flex", gap: 16, flexDirection: "column" }}>
              <div style={{ fontSize: 14, color: "#444" }}>
                {mapLoading ? "Fetching address..." : mapAddress}
              </div>

              {/* Map preview iframe (works without API key) */}
              {mapCoords ? (
                <div>
                  <iframe
                    title="Park location"
                    className="park-map-iframe"
                    src={`https://www.google.com/maps?q=${mapCoords.lat},${mapCoords.lng}&z=16&output=embed`}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
                    <button
                      onClick={() =>
                        navigator.clipboard
                          .writeText(mapAddress || `${mapCoords.lat},${mapCoords.lng}`)
                          .then(() => alert("Address copied"))
                          .catch(() => alert("Copy failed"))
                      }
                      style={{
                        backgroundColor: theme.colors.primary,
                        color: "#fff",
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      Copy Address
                    </button>

                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${mapCoords.lat},${mapCoords.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        textDecoration: "none",
                      }}
                    >
                      <button
                        style={{
                          backgroundColor: theme.colors.secondary,
                          color: "#fff",
                          padding: "8px 12px",
                          borderRadius: 8,
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        Open in Google Maps
                      </button>
                    </a>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "#666" }}>Map preview not available</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION */}
      {confirmDeleteOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 20,
          }}
          onClick={() => setConfirmDeleteOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 22,
              width: 380,
              boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
              animation: "modalPop 180ms ease",
              position: "relative",
            }}
          >
            <h3 style={{ marginTop: 0, color: theme.colors.grayDark }}>Confirm Delete</h3>
            <p style={{ color: "#444" }}>Are you sure you want to delete this attendance record?</p>

            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 18 }}>
              <button
                onClick={() => setConfirmDeleteOpen(false)}
                style={{
                  background: "#eee",
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>

              <button
                onClick={confirmDelete}
                style={{
                  background: "#e53935",
                  color: "#fff",
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParkAttendance;